import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import PageHead from "@/components/PageHead";
import { stripBold } from "@/lib/markdown";
import { renderContent } from "@/lib/blogRender";

/* Editorial article page — matches the approved visual reference:
    - copper eyebrow with the category
    - big near-black title
    - subtitle deck below
    - date | reading time meta
    - full-width hero image
    - comfortable reading column
    - `!! ...` markdown-lite marker renders the copper-bar pull-quote

   Order at the bottom (locked by product decision):
     1. body content
     2. CTA "Está a pensar fazer obras?" (template, not authored per article)
     3. Artigos relacionados (only shown when there are any) */

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("pt-PT", { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
};

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPost(null);
        setRelated([]);
        setNotFound(false);
        api.get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setNotFound(true));
        // Fetch related independently so a slow related endpoint never blocks
        // the article render. Silently fail: related is a nice-to-have.
        api.get(`/blog/${slug}/related`, { params: { limit: 3 } })
            .then((r) => setRelated(Array.isArray(r.data) ? r.data : []))
            .catch(() => setRelated([]));
    }, [slug]);

    if (notFound) {
        return (
            <>
                <PageHead
                    title="Artigo não encontrado — O Homem da Obra"
                    description="Este artigo pode ter sido despublicado ou o link estar errado."
                    noindex
                />
                <div className="max-w-2xl mx-auto py-32 px-6 text-center">
                    <h1 className="font-display text-3xl font-bold text-stone-900 mb-4">Artigo não encontrado</h1>
                    <p className="text-stone-600 mb-8">Este artigo pode ter sido despublicado ou o link estar errado.</p>
                    <Link to="/blog" className="text-[hsl(var(--copper))] underline underline-offset-4">Voltar ao Blog</Link>
                </div>
            </>
        );
    }

    if (!post) {
        return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">A carregar…</div>;
    }

    const cover = post.cover_id ? fileUrl(post.cover_id) : null;
    const coverAlt = post.cover_alt || post.title || "";

    return (
        <>
            <PageHead
                title={`${stripBold(post.seo_title || post.title)} — O Homem da Obra`}
                description={stripBold(post.seo_description || post.subtitle || post.excerpt || `${post.title} — artigo no blog O Homem da Obra.`)}
                canonical={`https://ohomemdaobra.pt/blog/${post.slug}`}
                ogImage={cover || undefined}
                type="article"
                publishedAt={post.published_at || undefined}
                author={post.author || undefined}
            />

            <article className="pt-10 sm:pt-16 pb-20 sm:pb-24">
                {/* Header + editorial masthead */}
                <div className="max-w-3xl mx-auto px-6 sm:px-10">
                    <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[hsl(var(--copper))] mb-10" data-testid="blog-back">
                        <ArrowLeft className="w-4 h-4" /> Voltar ao Blog
                    </Link>

                    {post.category && (
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold mb-5" data-testid="blog-category">
                            — {post.category}
                        </div>
                    )}

                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 tracking-tight leading-[1.05] break-words" data-testid="blog-post-title">
                        {post.title}
                    </h1>

                    {(post.subtitle || post.excerpt) && (
                        <p className="mt-6 text-lg sm:text-xl text-stone-600 leading-[1.6] max-w-2xl">
                            {post.subtitle || post.excerpt}
                        </p>
                    )}

                    <div className="mt-8 text-xs uppercase tracking-wider text-stone-500 flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span>{formatDate(post.published_at)}</span>
                        {post.reading_time_mins ? (
                            <>
                                <span aria-hidden>|</span>
                                <span>{post.reading_time_mins} min de leitura</span>
                            </>
                        ) : null}
                        {post.author && (
                            <>
                                <span aria-hidden>|</span>
                                <span>por {post.author}</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Hero */}
                {cover && (
                    <div className="max-w-5xl mx-auto px-6 sm:px-10 mt-10 sm:mt-12">
                        <div className="rounded-2xl sm:rounded-3xl overflow-hidden aspect-[16/9] bg-stone-100">
                            <img src={cover} alt={coverAlt} className="w-full h-full object-cover" />
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="max-w-[700px] mx-auto px-6 sm:px-10 mt-12 sm:mt-16" data-testid="blog-post-content">
                    {renderContent(post.content)}
                </div>

                {/* Template CTA — always present, not authored per article */}
                <div className="max-w-[700px] mx-auto px-6 sm:px-10 mt-16 sm:mt-20">
                    <div className="border-t border-stone-200 pt-10 sm:pt-14" data-testid="blog-post-cta">
                        <p className="font-display text-2xl sm:text-3xl font-black text-stone-900 tracking-tight leading-[1.15]">
                            Está a pensar fazer obras?
                        </p>
                        <p className="text-stone-600 text-base sm:text-lg mt-4 leading-relaxed">
                            Conte-nos o que pretende fazer. Analisamos a intervenção e ajudamos a perceber quais os trabalhos necessários.
                        </p>
                        <Link
                            to="/orcamento"
                            className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-stone-900 border-b-2 border-[hsl(var(--copper))] pb-1 hover:opacity-70 transition"
                            data-testid="blog-post-cta-link"
                        >
                            Pedir orçamento <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </div>
            </article>

            {/* Related — only rendered when there is at least one */}
            {related.length > 0 && (
                <section className="pb-24 sm:pb-32 border-t border-stone-200 pt-16 sm:pt-20" data-testid="blog-related">
                    <div className="max-w-5xl mx-auto px-6 sm:px-10">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-bold mb-8">
                            Artigos relacionados
                        </div>
                        <div className="grid sm:grid-cols-3 gap-10 sm:gap-8">
                            {related.map((p) => (
                                <Link key={p.id} to={`/blog/${p.slug}`} className="block group" data-testid={`blog-related-${p.slug}`}>
                                    {p.cover_id ? (
                                        <div className="rounded-xl overflow-hidden aspect-[16/10] bg-stone-100 mb-4">
                                            <img
                                                src={fileUrl(p.cover_id)}
                                                alt={p.cover_alt || p.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-700"
                                            />
                                        </div>
                                    ) : (
                                        <div className="rounded-xl aspect-[16/10] bg-stone-100 mb-4" />
                                    )}
                                    {p.category && (
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold mb-2">
                                            — {p.category}
                                        </div>
                                    )}
                                    <h3 className="font-display text-lg sm:text-xl font-bold text-stone-900 tracking-tight leading-[1.2] group-hover:opacity-80 transition break-words">
                                        {p.title}
                                    </h3>
                                    <div className="mt-3 text-[11px] uppercase tracking-wider text-stone-500">
                                        {formatDate(p.published_at)}
                                        {p.reading_time_mins ? ` · ${p.reading_time_mins} min` : ""}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}
